import {
  FacilityFlowCoreModulesFleetDtosVehicleTripRole as TripRoleEnum,
  FacilityFlowCoreModulesFleetEntitiesTripPurposeKind as PurposeKindEnum,
  FacilityFlowCoreModulesPlanningEntitiesDriverRole as PlannedRoleEnum,
  type FacilityFlowCoreModulesFleetDtosVehicleTripRole as TripRole,
  type FacilityFlowCoreModulesFleetEntitiesTripPurposeKind as PurposeKind,
  type FacilityFlowCoreModulesPlanningEntitiesDriverRole as PlannedRole,
} from '@/api/generated/models';
import { isoDate, parseUtcTimestamp } from '@/utils/dateUtils';
import { MAX_ODOMETER_KM } from '@/utils/logbookLimits';

/** Whole kilometres in range, or null while the field is empty or invalid. */
export function parseKm(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const km = Number(value.trim());
  return km <= MAX_ODOMETER_KM ? km : null;
}

/** What the trip dialog offers (FAFD-68): "Kein Fahrer" only together with the clock-in. */
export type TripChoiceRole = 'driver' | 'passenger' | 'none';
export type TripDialogMode = 'clockIn' | 'start';

export const DRIVER: TripRole = TripRoleEnum.NUMBER_1;
export const PASSENGER: TripRole = TripRoleEnum.NUMBER_2;
export const BUSINESS: PurposeKind = PurposeKindEnum.NUMBER_1;
export const PRIVATE: PurposeKind = PurposeKindEnum.NUMBER_2;

export function toTripRole(role: TripChoiceRole): TripRole | null {
  if (role === 'driver') return DRIVER;
  if (role === 'passenger') return PASSENGER;
  return null;
}

export interface TripChoice {
  role: TripChoiceRole;
  vehicleId: string;
}

export interface PlannedTrip {
  driverRole?: PlannedRole | null;
  vehicleId?: string | null;
}

interface InitialChoiceInput {
  mode: TripDialogMode;
  /** Role and plate the administration planned for the day (FACF-118), if any. */
  planned: PlannedTrip | null;
  activeVehicleIds: string[];
  /** The employee still has to park a vehicle — driving is refused until then. */
  driverBlocked: boolean;
}

/**
 * Preselection of the trip dialog: a planned role with license plate wins; otherwise "Kein
 * Fahrer" at clock-in and "Fahrer" when starting a trip on its own. A single vehicle needs no
 * choosing; a planned but deactivated vehicle is dropped.
 */
export function initialTripChoice({ mode, planned, activeVehicleIds, driverBlocked }: InitialChoiceInput): TripChoice {
  const single = activeVehicleIds.length === 1 ? (activeVehicleIds[0] ?? '') : '';
  const plannedVehicle =
    planned?.vehicleId && activeVehicleIds.includes(planned.vehicleId) ? planned.vehicleId : '';
  let role: TripChoiceRole = mode === 'clockIn' ? 'none' : 'driver';
  if (planned?.vehicleId && planned.driverRole === PlannedRoleEnum.NUMBER_1) role = 'driver';
  else if (planned?.vehicleId && planned.driverRole === PlannedRoleEnum.NUMBER_2) role = 'passenger';
  else if (planned?.driverRole === PlannedRoleEnum.NUMBER_3 && mode === 'clockIn') role = 'none';
  // why: a preselected "Fahrer" would only be refused (fleet.trips.vehicleNotParked).
  if (role === 'driver' && driverBlocked) role = mode === 'clockIn' ? 'none' : 'passenger';
  return { role, vehicleId: plannedVehicle || single };
}

interface SkipInput {
  /** A trip or ride of the employee is running (GET me/vehicle-trips/current). */
  hasRunningTrip: boolean;
  homeOffice: boolean;
  activeVehicleCount: number;
}

/** Clock-in without the vehicle question: a trip already runs, home office, or no vehicles. */
export function skipVehicleQuestion({ hasRunningTrip, homeOffice, activeVehicleCount }: SkipInput): boolean {
  return hasRunningTrip || homeOffice || activeVehicleCount === 0;
}

export interface TripStartInput {
  role: TripChoiceRole;
  vehicleId: string;
  /** The start reading as typed. */
  odometer: string;
  driverBlocked: boolean;
  purposeKind: PurposeKind;
  allowPrivateTrips: boolean;
}

/**
 * Whether the trip dialog may start: "Kein Fahrer" always; a passenger needs the plate; a
 * driver also a valid start reading, may not drive while a vehicle is not parked, and a private
 * trip only where the tenant allows it. Destination and purpose are asked when the trip ends.
 */
export function canConfirmTripStart(input: TripStartInput): boolean {
  if (input.role === 'none') return true;
  if (input.vehicleId === '') return false;
  if (input.role === 'passenger') return true;
  if (input.driverBlocked) return false;
  if (input.purposeKind === PRIVATE && !input.allowPrivateTrips) return false;
  return parseKm(input.odometer) !== null;
}

/** Which details a trip ends with, as the tenant set them (GET me/vehicle-trips/current). */
export interface TripEndRules {
  startRequired: boolean;
  destinationRequired: boolean;
  /** Only ever for a business trip. */
  purposeRequired: boolean;
}

/** Today's behaviour, also the backend's default: destination and purpose required, start not. */
export const DEFAULT_TRIP_END_RULES: TripEndRules = {
  startRequired: true,
  destinationRequired: true,
  purposeRequired: true,
};

/** The rules of a trip status; a field an older backend does not send keeps its default. */
export function tripEndRulesOf(status: Partial<Record<keyof TripEndRules, boolean | null>>): TripEndRules {
  const d = DEFAULT_TRIP_END_RULES;
  return {
    startRequired: status.startRequired ?? d.startRequired,
    destinationRequired: status.destinationRequired ?? d.destinationRequired,
    purposeRequired: status.purposeRequired ?? d.purposeRequired,
  };
}

export interface TripEndInput {
  /** A passenger only ends the ride — nothing to fill in. */
  isDriver: boolean;
  odometer: string;
  startLocation: string;
  destination: string;
  purpose: string;
  purposeKind: PurposeKind;
  needsArrival: boolean;
  /** The arrival as typed (HH:mm), or '' while empty. */
  arrivalTime: string;
  /** Omitted: the defaults (destination and purpose required). */
  rules?: TripEndRules;
}

/**
 * Whether "Fahrt beenden" may be sent (official logbook): start, destination and purpose as the
 * tenant requires them — the purpose only for a business trip — and the arrival time for a trip
 * from an earlier day. The reading is optional — without it the trip is "nicht abgestellt" — but
 * an invalid one never passes.
 */
export function canConfirmTripEnd(input: TripEndInput): boolean {
  if (!input.isDriver) return true;
  if (input.odometer.trim() !== '' && parseKm(input.odometer) === null) return false;
  const rules = input.rules ?? DEFAULT_TRIP_END_RULES;
  if (rules.startRequired && input.startLocation.trim() === '') return false;
  if (rules.destinationRequired && input.destination.trim() === '') return false;
  // why: a private trip has no purpose in the official logbook, whatever the tenant requires.
  if (rules.purposeRequired && input.purposeKind !== PRIVATE && input.purpose.trim() === '') return false;
  return !input.needsArrival || /^\d{2}:\d{2}$/.test(input.arrivalTime);
}

/** What the duty plan suggests for a business trip (FAFD-68); `placeholder` is "laut Dienstplan". */
export interface TripTextSuggestion {
  destination?: string | null;
  purpose?: string | null;
  placeholder?: string | null;
}

/**
 * The prefill of destination or purpose: the trip's own text, unless it is empty or still the
 * placeholder — then the duty plan's suggestion, so a plan entered after the start reaches the trip.
 */
export function prefillTripText(value: string | null | undefined, suggested: string | null | undefined, placeholder?: string | null): string {
  const own = value?.trim() ?? '';
  const isPlaceholder = own !== '' && placeholder != null && own.toLowerCase() === placeholder.trim().toLowerCase();
  if ((own === '' || isPlaceholder) && suggested) return suggested;
  return value ?? '';
}

/** The texts of a trip that are prefilled (FAFD-68). */
export interface TripTexts {
  startLocation: string;
  destination: string;
  purpose: string;
}

/**
 * A newer suggestion (`next`) replaces only the fields that still hold the previous one
 * (`filled`) — what the driver typed meanwhile stays.
 */
export function applyTripSuggestion<T extends TripTexts>(values: T, filled: TripTexts, next: TripTexts): T {
  const keep = (key: keyof TripTexts) => (values[key] === filled[key] ? next[key] : values[key]);
  return { ...values, startLocation: keep('startLocation'), destination: keep('destination'), purpose: keep('purpose') };
}

/**
 * The destination after switching between business and private: the duty plan's destination
 * belongs to a business trip, so it goes away for a private one and comes back — as long as the
 * driver has not typed their own.
 */
export function destinationForKind(destination: string, kind: PurposeKind, suggested: string | null | undefined): string {
  const plan = suggested ?? '';
  const untouched = destination.trim() === '' || destination === plan;
  if (!untouched) return destination;
  return kind === PRIVATE ? '' : plan;
}

/** A trip that started on an earlier local day needs its arrival — "now" would be wrong. */
export function needsArrivalTime(startedAt: string | null | undefined, now: Date): boolean {
  const start = parseUtcTimestamp(startedAt);
  return start !== null && isoDate(start) < isoDate(now);
}

interface DatedTrip {
  role?: TripRole;
  startedAt?: string | null;
  endedAt?: string | null;
  endOdometerKm?: number | null;
}

/**
 * Whether a trip or ride still runs: no arrival yet — but a trip the administration closed with an
 * end reading alone has ended, even without an arrival time (as the backend's IsOpen).
 */
export function isTripRunning(trip: DatedTrip): boolean {
  if (trip.endedAt) return false;
  return trip.role !== TripRoleEnum.NUMBER_1 || trip.endOdometerKm == null;
}

/**
 * Trips per local day (YYYY-MM-DD). A trip across midnight belongs to every day it touches, so
 * the day's read line shows it on both; a running trip counts up to `now`, one closed without
 * arrival only to its start.
 */
export function tripsByLocalDay<T extends DatedTrip>(trips: readonly T[], now: Date): Map<string, T[]> {
  const byDay = new Map<string, T[]>();
  for (const trip of trips) {
    const start = parseUtcTimestamp(trip.startedAt);
    if (!start) continue;
    const end = parseUtcTimestamp(trip.endedAt) ?? (isTripRunning(trip) ? now : start);
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const lastDay = isoDate(end < start ? start : end);
    for (let iso = isoDate(day); iso <= lastDay; day.setDate(day.getDate() + 1), iso = isoDate(day)) {
      byDay.set(iso, [...(byDay.get(iso) ?? []), trip]);
    }
  }
  return byDay;
}

/** Filter of the tab "Meine Fahrten": all, only own trips (driver), only rides (passenger). */
export type TripRoleFilter = 'all' | 'driver' | 'passenger';

interface RoleTrip {
  role?: TripRole | null;
  distanceKm?: number | null;
}

/** The trips of the chosen role, order kept. */
export function filterTripsByRole<T extends RoleTrip>(trips: readonly T[], filter: TripRoleFilter): T[] {
  if (filter === 'all') return [...trips];
  const role = filter === 'driver' ? DRIVER : PASSENGER;
  return trips.filter((trip) => trip.role === role);
}

/**
 * Distance of a trip as shown to the driver. An end reading below the start reading (a typo the
 * administration corrects in the logbook) has no distance yet, like in the logbook totals.
 */
export function shownDistanceKm(trip: Pick<RoleTrip, 'distanceKm'>): number | null {
  return trip.distanceKm != null && trip.distanceKm >= 0 ? trip.distanceKm : null;
}

/**
 * Kilometres driven as driver. A ride never counts — the employee did not drive it, and its
 * kilometres already belong to the driver's trip. Negative distances are left out (see shownDistanceKm).
 */
export function drivenKm(trips: readonly RoleTrip[]): number {
  return trips.reduce((sum, trip) => (trip.role === DRIVER ? sum + (shownDistanceKm(trip) ?? 0) : sum), 0);
}
